/**
 * NumberedMaterialGroup — numbered material cards (① ② ③ …) with an optional
 * "Also used in this application" sub-group (2726). Numbering is continuous
 * across the primary and secondary groups. Each card's CTA uses the shared
 * CX-2764 ContentLink (stub today).
 */

import { ContentLink } from "components/content-link";
import type { NumberedMaterialCard } from "../chapter.types";
import "./NumberedMaterialGroup.css";

interface NumberedMaterialGroupProps {
  title?: string;
  primary: NumberedMaterialCard[];
  secondary?: NumberedMaterialCard[];
}

const MaterialCardItem = ({
  card,
  index,
}: {
  card: NumberedMaterialCard;
  index: number;
}) => (
  <article className="numbered-material-card">
    <div className="numbered-material-card__head">
      <span className="numbered-material-card__number" aria-hidden="true">
        {index}
      </span>
      <h4 className="numbered-material-card__name">{card.name}</h4>
    </div>
    {card.description && (
      <p className="numbered-material-card__description">{card.description}</p>
    )}
    {card.link && <ContentLink {...card.link} />}
  </article>
);

export const NumberedMaterialGroup = ({
  title,
  primary,
  secondary,
}: NumberedMaterialGroupProps) => (
  <section className="numbered-material-group" aria-label={title}>
    {title && <h3 className="numbered-material-group__title">{title}</h3>}

    <div className="numbered-material-group__grid">
      {primary.map((card, i) => (
        <MaterialCardItem key={card.name} card={card} index={i + 1} />
      ))}
    </div>

    {secondary && secondary.length > 0 && (
      <>
        <p className="numbered-material-group__subheading">
          Also used in this application
        </p>
        <div className="numbered-material-group__grid">
          {secondary.map((card, i) => (
            <MaterialCardItem
              key={card.name}
              card={card}
              index={primary.length + i + 1}
            />
          ))}
        </div>
      </>
    )}
  </section>
);
